/**
 * Ceiling for one attempt at a repeatable native read.
 *
 * Inside an armed presentation it caps the attempt so a lost reply still leaves
 * room for another read before the deadline. Outside one it is the plain bound
 * a read is given so that it settles at all: there is no budget to retry in,
 * but a read that never settles is not a terminal outcome either.
 */
export const MAX_NATIVE_READ_ATTEMPT_MS = 3_000
const PAGE_LOADING_RETRY_DELAY_MS = 100
const NEVER_REPLAY = Symbol.for('forest.page-loading.never-replay')

type RecoveryPhase = 'idle' | 'active' | 'ready' | 'cancelled'

let deadline: number | undefined
let phase: RecoveryPhase = 'idle'
let generation = 0
// Aborted by reveal and by leaving the page: the lifetime of reveal-gated work.
let cancellation = new AbortController()
// Aborted only when the page is actually left. Reveal aborts `cancellation`,
// which is the right boundary for work that gates the reveal; an operation that
// legitimately outlives the reveal (daily paywall product initialization, the
// walkthrough's permission-status read) waits on this one instead, so the
// page's own lifetime still bounds it (spec Decision 10).
let lifetime = new AbortController()

export interface PageVisiblePayload {
  remaining_timeout_ms?: number | null
}

export interface PageLoadingAttempt {
  generation: number
  timeout: number
}

interface RetryInstruction extends Error {
  waitForPageLoadingRetry: () => Promise<void>
}

/**
 * Scope of a recovering operation inside one page-loading attempt.
 *
 * By default an operation is reveal-gated: it is a prerequisite of the page's
 * own reveal condition, so revealing ends its recovery. `survivesReveal` marks
 * startup work that legitimately outlives reveal — the walkthrough's
 * notification permission status read, or product work still pending after
 * content appears. Such work keeps the original deadline; only that deadline
 * or leaving the page ends it. It never extends the budget.
 */
export interface PageLoadingRecoveryScope {
  survivesReveal?: boolean
}

export function beginPageLoadingRecovery(
  payload: PageVisiblePayload | undefined,
): void {
  if (phase !== 'idle') return

  const remaining = payload?.remaining_timeout_ms
  if (
    typeof remaining !== 'number' ||
    !Number.isFinite(remaining) ||
    remaining < 0
  ) {
    return
  }

  deadline = performance.now() + remaining
  phase = 'active'
}

export function markPageLoadingReady(): void {
  if (phase !== 'active') return
  phase = 'ready'
  generation += 1
  cancellation.abort()
}

export function cancelPageLoadingRecovery(): void {
  if (phase === 'cancelled') return
  phase = 'cancelled'
  generation += 1
  cancellation.abort()
  lifetime.abort()
}

/**
 * Begin a bounded attempt at a verified repeatable native read (user info,
 * notification permission status). Returns `undefined` when this read is not
 * inside a live page-loading attempt, so the caller keeps its previous call
 * shape: when no native presentation armed a budget, when reveal has ended a
 * reveal-gated scope, and — the case `survivesReveal` reaches — when the
 * original budget is spent.
 *
 * That last case matters beyond the pages this spec covers.
 * `survivesReveal` keeps a scope recovering in phase `ready`, and only leaving
 * the page ends it, so without this guard a read minutes past the deadline
 * still got an attempt whose timeout was `floor(remaining / 2)` — zero. Bifrost
 * honours an explicit zero as a real deadline, so the call was dispatched and
 * abandoned on the next macrotask and the read failed with `Page loading has
 * ended`. The shared notification-status read is called after a purchase by
 * nine paywalls that swallow its rejection, so every user who browsed longer
 * than the cover budget silently lost the grant-permission dialog. An attempt
 * that cannot fit is not an attempt (spec Decision 4); the read belongs on the
 * pre-existing path instead.
 */
export function beginNativeReadAttempt(
  maxAttemptMs: number = MAX_NATIVE_READ_ATTEMPT_MS,
  scope: PageLoadingRecoveryScope = {},
): PageLoadingAttempt | undefined {
  if (phase === 'cancelled') throw pageLoadingEndedError()
  if (!isRecovering(scope) || deadline === undefined) return undefined

  const remaining = Math.max(0, deadline - performance.now())
  // The same floor `pageLoadingRetryInstruction` declines below, so the two
  // sides of one budget agree on when it is spent.
  if (remaining <= PAGE_LOADING_RETRY_DELAY_MS * 2) return undefined

  return {
    generation,
    // A lost reply must reject early enough for another safe read.
    timeout: Math.min(maxAttemptMs, Math.floor(remaining / 2)),
  }
}

export function assertPageLoadingAttemptIsActive(
  attempt: PageLoadingAttempt,
  scope: PageLoadingRecoveryScope = {},
): void {
  if (deadline === undefined || performance.now() >= deadline) {
    throw pageLoadingEndedError()
  }
  if (!isRecovering(scope)) throw pageLoadingEndedError()
  // Reveal bumps the generation, so work that outlives reveal is bounded by
  // the phase and the deadline above rather than by the attempt's generation.
  if (!scope.survivesReveal && generation !== attempt.generation) {
    throw pageLoadingEndedError()
  }
}

export function nativeReadRetryInstruction(
  attempt: PageLoadingAttempt,
  cause: unknown,
  scope: PageLoadingRecoveryScope = {},
): RetryInstruction | undefined {
  assertPageLoadingAttemptIsActive(attempt, scope)
  return pageLoadingRetryInstruction(cause, scope)
}

/** Whether the given scope is still recovering in the current phase. */
function isRecovering(scope: PageLoadingRecoveryScope): boolean {
  if (phase === 'active') return true
  return phase === 'ready' && scope.survivesReveal === true
}

/**
 * Mark a failure that page-loading recovery must never replay: the operation
 * may already have taken effect (a permission prompt, a purchase), or its own
 * attempt budget is spent. The caller's own error still reaches its existing
 * terminal handling.
 */
export function markNeverReplay<E>(error: E): E {
  if (typeof error === 'object' && error !== null) {
    Object.defineProperty(error, NEVER_REPLAY, { value: true })
  }
  return error
}

function isNeverReplay(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as Record<symbol, unknown>)[NEVER_REPLAY] === true
  )
}

/**
 * Whether the page-loading budget still allows another attempt at a
 * prerequisite. An unarmed presentation (no native budget) reports `true` so an
 * existing retry policy keeps its own behaviour untouched. Reveal does not end
 * the budget: a page whose product work is still pending after its content
 * appears keeps the original deadline. Leaving the page does end it.
 */
export function hasPageLoadingBudget(): boolean {
  if (phase === 'cancelled') return false
  if (deadline === undefined) return true
  return performance.now() < deadline
}

/**
 * Whether the page has been left. Distinct from the budget: an expired deadline
 * still belongs to a present page, so its operation may run its own terminal
 * behaviour, while a page that is already gone must not produce another
 * terminal callback or mutate its successor.
 */
export function hasPageLoadingEnded(): boolean {
  return phase === 'cancelled'
}

/**
 * Retry a verified repeatable prerequisite read inside the original page
 * budget. A rejection is retried only while the attempt is still the live one
 * and the budget can fit another try; otherwise the caller sees its own error
 * and the page's existing terminal policy applies.
 */
export async function withPageLoadingRecovery<T>(
  run: () => Promise<T>,
  scope: PageLoadingRecoveryScope = {},
): Promise<T> {
  while (true) {
    try {
      return await run()
    } catch (error) {
      if (isNeverReplay(error)) throw error
      const instruction = isRetryInstruction(error)
        ? error
        : pageLoadingRetryInstruction(error, scope)
      if (!instruction) throw error
      await instruction.waitForPageLoadingRetry()
    }
  }
}

function isRetryInstruction(error: unknown): error is RetryInstruction {
  return (
    typeof error === 'object' &&
    error !== null &&
    'waitForPageLoadingRetry' in error &&
    typeof (error as RetryInstruction).waitForPageLoadingRetry === 'function'
  )
}

function pageLoadingRetryInstruction(
  cause: unknown,
  scope: PageLoadingRecoveryScope = {},
): RetryInstruction | undefined {
  if (!isRecovering(scope) || deadline === undefined) return undefined
  const remaining = deadline - performance.now()
  if (remaining <= PAGE_LOADING_RETRY_DELAY_MS * 2) return undefined

  const error = new Error('Retry page loading prerequisite', {
    cause,
  }) as RetryInstruction
  error.waitForPageLoadingRetry = () =>
    new Promise<void>((resolve, reject) => {
      const signal = scope.survivesReveal
        ? lifetime.signal
        : cancellation.signal
      if (signal.aborted) {
        reject(pageLoadingEndedError())
        return
      }

      const onAbort = () => {
        clearTimeout(timer)
        reject(pageLoadingEndedError())
      }
      const timer = setTimeout(() => {
        signal.removeEventListener('abort', onAbort)
        resolve()
      }, PAGE_LOADING_RETRY_DELAY_MS)
      signal.addEventListener('abort', onAbort, { once: true })
    })
  return error
}

export function resetPageLoadingRecoveryForTests(): void {
  deadline = undefined
  phase = 'idle'
  generation = 0
  cancellation = new AbortController()
  lifetime = new AbortController()
}

function pageLoadingEndedError(): Error {
  return new Error('Page loading has ended')
}
